import { useSignIn } from '@clerk/react';
import { Button, LoadingIndicator } from '@vrplatform/components';
import {
  captureSentryError,
  useOwnerPortal,
  getOwnerPortalReturnUrl,
} from '@vrplatform/data';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import { showErrorNotification } from '@vrplatform/theme';
import { Center, Text } from '@mantine/core';
import { Loader } from '@mantine/core';
// Handle magic link verification results
import {
  type ReactNode,
  startTransition,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router';
import { EmptyViewWrapper } from '../../components';
import {
  getClerkErrorCode,
  getClerkErrorMessage,
  type SecondFactorSubmission,
  verifyAndFinalizeSecondFactor,
} from '../onboarding/hooks/useSignInForm.utils';
import { VerifyTokenSecondFactorView } from './VerifyTokenSecondFactorView';

type VerifyTokenViewProps = {
  isAuthenticated?: boolean;
};

export const VerifyTokenView = ({ isAuthenticated }: VerifyTokenViewProps) => {
  if (isAuthenticated) return <Authenticated />;

  return <Verify />;
};

const AuthContainer = ({
  bgColor,
  children,
  loading,
}: {
  bgColor: string;
  children?: ReactNode;
  loading: boolean;
}) => {
  return (
    <Center
      sx={{
        minHeight: '100vh',
        minWidth: '100vw',
        backgroundColor: bgColor,
      }}
    >
      {loading ? <LoadingIndicator isFullPageLoading={false} /> : children}
    </Center>
  );
};

const Authenticated = () => {
  return <Navigate to="/" replace />;
};

const Verify = () => {
  const portal = useOwnerPortal();
  const goto = useNavigate();

  const isVerifying = useRef(false);
  const [verification, setVerification] = useState<{
    status: 'loading' | 'needs_second_factor' | 'verified' | 'failed';
    message?: string;
  }>({ status: 'loading', message: 'Test' });

  const [token] = useQueryState('token', parseAsString);
  const [searchParams] = useSearchParams();

  const { signIn } = useSignIn();

  useEffect(() => {
    async function verify() {
      try {
        const { error: ticketError } = await signIn.ticket({
          ticket: token || '',
        });

        if (ticketError) throw ticketError;

        if (signIn.status === 'needs_second_factor') {
          setVerification({ status: 'needs_second_factor' });
          return;
        }

        if (signIn.status !== 'complete') {
          throw new Error(`Failed to verify token: ${signIn.status}`);
        }

        const { error: finalizeError } = await signIn.finalize();

        if (finalizeError) throw finalizeError;

        setVerification({ status: 'verified' });
      } catch (err: unknown) {
        console.error(err);
        // PRO-19449: a reopened tab or an old link can hold an expired or
        // already used ticket. The page explains this, so Sentry gets no report.
        if (
          [
            'ticket_expired_code',
            'sign_in_token_already_used_code',
            'sign_in_token_cannot_be_used_code',
            'sign_in_token_revoked_code',
          ].includes(getClerkErrorCode(err) ?? '')
        ) {
          setVerification({
            status: 'failed',
            message:
              'This sign-in link has expired or was already used. Please sign in, or ask for a new link.',
          });
          return;
        }

        const message = getClerkErrorMessage(
          err,
          'We failed to process your request. Please contact support if the issue persists.'
        );

        captureSentryError(err);
        setVerification({ status: 'failed', message });
      }
    }
    if (token && !isVerifying.current) {
      isVerifying.current = true;
      verify();
    }
  }, [token, signIn, signIn.status]);

  const submitSecondFactor = async (input: SecondFactorSubmission) => {
    try {
      const status = await verifyAndFinalizeSecondFactor(signIn, input);

      if (status !== 'complete') {
        throw new Error(`Failed to verify second factor: ${status}`);
      }

      setVerification({ status: 'verified' });
    } catch (err: unknown) {
      captureSentryError(err);
      showErrorNotification({
        title: 'Failed to submit authentication',
        message: getClerkErrorMessage(err, 'Please try again.'),
      });
    }
  };

  if (!token)
    return (
      <EmptyViewWrapper
        title={'Verification failed'}
        description={'Please contact support if the issue persists.'}
        actions={
          <Button
            variant="primary"
            onClick={() => startTransition(() => goto('/'))}
          >
            Return to login
          </Button>
        }
      />
    );

  if (verification.status === 'loading') {
    return (
      <EmptyViewWrapper>
        <Loader type="dots" size="md" />
        <Text c="gray" ta="center" mt="lg" size="lg">
          Verifying...
        </Text>
      </EmptyViewWrapper>
    );
  }

  if (verification.status === 'needs_second_factor') {
    return (
      <VerifyTokenSecondFactorView
        submitSecondFactor={submitSecondFactor}
        returnToDefault={() => goto('/login')}
      />
    );
  }

  if (verification.status === 'failed') {
    return (
      <EmptyViewWrapper
        title={'Verification failed'}
        description={
          verification.message ||
          'Please contact support if the issue persists.'
        }
        actions={
          <Button
            variant="primary"
            onClick={() => startTransition(() => goto('/'))}
          >
            Return to login
          </Button>
        }
      />
    );
  }

  if (verification.status === 'verified') {
    if (portal) {
      const returnUrl = getOwnerPortalReturnUrl(
        searchParams.get('redirect_url'),
        portal.hostname
      );
      return (
        <Navigate
          to={`/owner-login?owner_portal=${portal.hostname}&redirect_url=${encodeURIComponent(returnUrl)}`}
          replace
        />
      );
    }
    const path = searchParams.get('path');
    const search = searchParams.toString();
    const to = path && path !== '/' ? `${path || ''}?${search}` : `/?${search}`;

    return <Navigate to={to} replace />;
  }

  return (
    <AuthContainer bgColor="#fff" loading={false}>
      <Text>Something went wrong</Text>
    </AuthContainer>
  );
};
